// src/services/eventService.js
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    query,
    where,
    orderBy,
    serverTimestamp,
    arrayUnion,
    arrayRemove,
} from "firebase/firestore";
import { db } from "../firebase/config";

// ========== EVENT CRUD ==========

export const createEvent = async (eventData) => {
    try {
        const eventRef = await addDoc(collection(db, "events"), {
            ...eventData,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            registrations: [],
            attendanceMarked: false,
            certificatesUploaded: false,
            eventStatus: "Upcoming", // Upcoming | Ongoing | Completed | Closed
        });
        return { success: true, eventId: eventRef.id };
    } catch (error) {
        console.error("Error creating event:", error);
        throw error;
    }
};

export const getAllEvents = async () => {
    try {
        const eventsRef = collection(db, "events");
        const q = query(eventsRef, orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
        console.error("Error fetching events:", error);
        throw error;
    }
};

export const getEventById = async (eventId) => {
    try {
        const ref = doc(db, "events", eventId);
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        return { id: snap.id, ...snap.data() };
    } catch (error) {
        console.error("Error fetching event:", error);
        throw error;
    }
};

export const updateEvent = async (eventId, data) => {
    try {
        const ref = doc(db, "events", eventId);
        await updateDoc(ref, {
            ...data,
            updatedAt: serverTimestamp(),
        });
        return { success: true };
    } catch (error) {
        console.error("Error updating event:", error);
        throw error;
    }
};

export const deleteEvent = async (eventId) => {
    try {
        const ref = doc(db, "events", eventId);
        await deleteDoc(ref);
        return { success: true };
    } catch (error) {
        console.error("Error deleting event:", error);
        throw error;
    }
};

export const setEventStatus = async (eventId, status) => {
    try {
        const ref = doc(db, "events", eventId);
        await updateDoc(ref, {
            eventStatus: status, // Upcoming | Ongoing | Completed | Closed
            updatedAt: serverTimestamp(),
        });
        return { success: true };
    } catch (error) {
        console.error("Error setting event status:", error);
        throw error;
    }
};

// ========== REGISTRATION ==========

export const registerForEvent = async (eventId, registrationData) => {
    try {
        const ref = doc(db, "events", eventId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
            throw new Error("Event not found");
        }

        const event = snap.data();
        const registrations = event.registrations || [];

        if (event.eventStatus === "Closed") {
            return { success: false, message: "Registrations closed for this event" };
        }

        const already = registrations.some(
            (r) =>
                r.studentId === registrationData.studentId ||
                r.email === registrationData.email
        );
        if (already) {
            return { success: false, message: "Already registered for this event" };
        }

        if (event.maxCapacity && registrations.length >= event.maxCapacity) {
            return { success: false, message: "Event is full" };
        }
        const uid = registrationData.userId || registrationData.uid || null;
        if ((event.type || "internal") === "internal" && uid) {
            const userSnap = await getDoc(doc(db, "users", uid));
            const credits = userSnap.exists() ? Number(userSnap.data().totalCredits || 0) : 0;
            const cap = Number(event.credit_cap || 0);
            if (credits < cap) {
                return { success: false, message: `Not eligible: requires ${cap} credits` };
            }
        }

        const registrationPayload = {
            ...registrationData,
            registeredAt: new Date().toISOString(),
            attended: false,
            certificateUrl: null,
        };
        try {
            await updateDoc(ref, {
                registrations: arrayUnion(registrationPayload),
            });
            return { success: true, message: "Successfully registered" };
        } catch (writeErr) {
            if (!uid) {
                throw writeErr;
            }
            const regRef = collection(db, "event_registrations");
            const existingSnap = await getDocs(query(regRef, where("eventId", "==", eventId), where("userId", "==", uid)));
            if (!existingSnap.empty) {
                return { success: false, message: "Already registered for this event" };
            }
            await addDoc(regRef, {
                eventId,
                userId: uid,
                studentId: registrationData.studentId || uid,
                name: registrationData.name || "",
                email: registrationData.email || "",
                course: registrationData.course || "",
                year: registrationData.year || "",
                mobileNo: registrationData.mobileNo || "",
                status: "registered",
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            return { success: true, message: "Successfully registered" };
        }
    } catch (error) {
        console.error("Error registering for event:", error);
        throw error;
    }
};

export const cancelRegistration = async (eventId, studentId) => {
    try {
        const ref = doc(db, "events", eventId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
            throw new Error("Event not found");
        }

        const event = snap.data();
        const registrations = event.registrations || [];
        const registration = registrations.find(
            (r) => r.studentId === studentId
        );

        if (!registration) {
            return { success: false, message: "Registration not found" };
        }

        await updateDoc(ref, {
            registrations: arrayRemove(registration),
        });

        return { success: true };
    } catch (error) {
        console.error("Error canceling registration:", error);
        throw error;
    }
};

// FIXED: Better matching logic for getUserEvents
export const getUserEvents = async (userIdentifier) => {
    try {
        // console.log("🔍 getUserEvents called with identifier:", userIdentifier);

        const ref = collection(db, "events");
        const snap = await getDocs(ref);
        const events = [];

        // console.log("📦 Total events in database:", snap.docs.length);

        snap.docs.forEach((d) => {
            const event = { id: d.id, ...d.data() };
            const registrations = event.registrations || [];

            // console.log(`📋 Event "${event.title}" has ${registrations.length} registrations`);

            // Match by studentId, email, or uid
            const reg = registrations.find((r) => {
                const matchStudentId = r.studentId === userIdentifier;
                const matchEmail = r.email === userIdentifier;

                if (matchStudentId || matchEmail) {
                    // console.log(`✅ MATCH FOUND in "${event.title}":`, {
                    //     registrationStudentId: r.studentId,
                    //     registrationEmail: r.email,
                    //     searchIdentifier: userIdentifier,
                    //     matchType: matchStudentId ? "studentId" : "email"
                    // });
                }

                return matchStudentId || matchEmail;
            });

            if (reg) {
                events.push({
                    ...event,
                    userRegistration: reg,
                });
            }
        });

        // console.log("✅ Found user events:", events.length);
        // console.log("📊 Event details:", events.map(e => ({
        //     title: e.title,
        //     hasRegistration: !!e.userRegistration,
        //     attended: e.userRegistration?.attended,
        //     certificateUrl: e.userRegistration?.certificateUrl
        // })));

        events.sort((a, b) => {
            const aTime = a.createdAt?.seconds || 0;
            const bTime = b.createdAt?.seconds || 0;
            return bTime - aTime;
        });

        return events;
    } catch (error) {
        console.error("❌ Error fetching user events:", error);
        throw error;
    }
};

// ========== ATTENDANCE ==========

export const bulkMarkAttendance = async (eventId, attendanceData) => {
    try {
        const ref = doc(db, "events", eventId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
            throw new Error("Event not found");
        }

        const event = snap.data();
        const registrations = event.registrations || [];

        const updated = registrations.map((reg) => {
            const att = attendanceData.find(
                (a) => a.studentId === reg.studentId
            );
            return att ? { ...reg, attended: att.attended } : reg;
        });

        await updateDoc(ref, {
            registrations: updated,
            attendanceMarked: true,
        });

        return { success: true };
    } catch (error) {
        console.error("Error bulk marking attendance:", error);
        throw error;
    }
};

// ========== CERTIFICATES ==========

export const uploadCertificates = async (eventId, certificateData) => {
    try {
        const ref = doc(db, "events", eventId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
            throw new Error("Event not found");
        }

        const event = snap.data();
        const registrations = event.registrations || [];

        const updated = registrations.map((reg) => {
            const cert = certificateData.find(
                (c) => c.studentId === reg.studentId
            );
            return cert && cert.certificateUrl
                ? { ...reg, certificateUrl: cert.certificateUrl }
                : reg;
        });

        await updateDoc(ref, {
            registrations: updated,
            certificatesUploaded: true,
        });

        return { success: true };
    } catch (error) {
        console.error("Error uploading certificates:", error);
        throw error;
    }
};

export const getAttendanceSheet = async (eventId) => {
    try {
        const event = await getEventById(eventId);
        if (!event) {
            throw new Error("Event not found");
        }

        const registrations = event.registrations || [];

        return registrations.map((reg) => ({
            name: reg.name,
            studentId: reg.studentId,
            email: reg.email,
            course: reg.course,
            year: reg.year,
            mobileNo: reg.mobileNo,
            attended: reg.attended ? "Yes" : "No",
            certificateUrl: reg.certificateUrl || "",
        }));
    } catch (error) {
        console.error("Error creating attendance sheet:", error);
        throw error;
    }
};
